// @flow
import RootNodeProvider from "../RootNodeProvider";
import {
    currentRouterPath,
    emitInternalEvent,
    takeCategorySelectionSource,
    toDeliveryMethod,
    toProductDetails,
} from "./internalEvents";
import {findDeepestVehiclePath} from "../vehicleKey";
import {getRouterMode} from "../routerMode";
import {CouponState} from "../../models/Coupon";
import {CHECKOUT_PAGE_STATUS} from "../../components/checkout/CheckoutPage";
import {brandSelected, configSet} from "../../reducers/configReducer";
import {
    categoryReset,
    categorySet,
    filterSet,
    markerSet,
    searchSet,
    sortSet,
    vehicleReset,
    vehicleSet,
} from "../../reducers/filterReducer";
import {productSet} from "../../reducers/contentReducer";
import {fromCartRemoved, quantitySet, toCartAdded} from "../../reducers/cartReducer";
import {bookmarkAdded, bookmarkRemoved} from "../../reducers/bookmarksReducer";
import {enquirySaved, shareButtonClicked, wishlistSharingSaved} from "../../reducers/mailingReducer";
import {cartSuccessChecked, checkoutPageStepSet, paymentSet, paymentSuccessVerified} from "../../reducers/orderReducer";
import {couponSuccessChecked} from "../../reducers/couponReducer";

let moduleInitEmitted = false;

const vehiclePathTitles = (state, vehicleKey, fallback): string[] => {
    const series = ((state.filter || {}).filterStats || {}).vehicleSeries || [];
    const path = findDeepestVehiclePath(series, node => node.key === vehicleKey);
    if (path.length) return path.map(node => node.title);
    return fallback ? [fallback] : [];
};

const categoryPathTitles = (state, categoryKey, fallback): string[] => {
    const mains = ((state.filter || {}).filterStats || {}).categories || [];
    for (const main of mains) {
        if (main.key === categoryKey) return [main.title];
        for (const sub of main.categories || []) {
            if (sub.key === categoryKey) return [main.title, sub.title];
        }
    }
    return fallback ? [fallback] : [];
};

const sortingName = (sort): string => {
    if (!sort || sort.fieldname === "score") return "popular";
    const direction = sort.direction === "ASC" ? "asc" : "desc";
    return sort.fieldname === "price" ? `price_${direction}` : `name_${direction}`;
};

const cartProducts = state => (state.cart.items || []).map(item =>
    toProductDetails(item.product, {amount: item.amount, shippingMethod: item.shippingMethod}));

const emitFilterChanged = state => {
    const filter = state.filter.filter || {};
    emitInternalEvent("filter_changed", {
        only_sale: (filter.markers || []).includes("SALE"),
        sorting: sortingName(filter.sort),
        category: categoryPathTitles(state, filter.categoryKey, filter.selectedCategory),
        model: filter.vehicleKey ? vehiclePathTitles(state, filter.vehicleKey, null) : [],
        price_min: filter.priceRange ? filter.priceRange.from : null,
        price_max: filter.priceRange ? filter.priceRange.to : null,
    }, state);
};

const orderProducts = order => (order.order.orderItems || []).map(item => ({
    product_ref_no: item.product.id || null,
    article_no: item.product.productNo,
    title: item.product.productName,
    price: item.product.unitPrice.pricePerUnit.cost,
    currency: item.product.unitPrice.pricePerUnit.currency.code,
    amount: item.amount.amount,
    delivery_method: toDeliveryMethod(item.shippingMethod),
}));

export const internalEvents = (store: any) => (next: any) => (action: any) => {
    // values that the reducer is about to change
    let removedCartItem = null;
    let previousCheckoutPage = null;
    let previousQuantity = null;
    try {
        if (action.type === fromCartRemoved.type) {
            removedCartItem = store.getState().cart.items.find(item => item.product.id === action.payload.id);
        }
        if (action.type === quantitySet.type) {
            const item = store.getState().cart.items.find(item => item.product.id === action.payload.id);
            previousQuantity = item ? item.amount : null;
        }
        if (action.type === checkoutPageStepSet.type) {
            previousCheckoutPage = store.getState().order.currentPage;
        }
    } catch (e) {
        console.error(e);
    }

    const result = next(action);

    try {
        const state = store.getState();
        switch (action.type) {
            case configSet.type: {
                if (!moduleInitEmitted) {
                    moduleInitEmitted = true;
                    const root = RootNodeProvider.getRootNode();
                    emitInternalEvent("module_init", {
                        data_params: root && root.dataset ? {...root.dataset} : {},
                        url_params: Object.fromEntries(new URLSearchParams(window.location.search)),
                        initial_path: currentRouterPath(),
                        router_mode: getRouterMode(),
                    }, state);
                    emitInternalEvent("navigate", {
                        router_path: currentRouterPath(),
                        full_url: window.location.href,
                    }, state);
                }
                break;
            }
            case brandSelected.type: {
                const {brand, selectionSource} = action.payload || {};
                if (brand && brand.manufacturerName) {
                    emitInternalEvent("brand_selected", {
                        selected_brand: brand.manufacturerName,
                        selection_source: selectionSource || "brand_select_screen",
                    }, state);
                }
                break;
            }
            case vehicleSet.type: {
                const selected = action.payload.vehicleModel || action.payload.vehicle;
                const vehicleKey = action.payload.vehicleKey || (selected ? selected.key : null);
                if (vehicleKey || selected) {
                    emitInternalEvent("vehicle_filtered", {
                        selected_carline: vehiclePathTitles(state, vehicleKey, selected ? selected.title : null),
                        selection_source: action.payload.selectionSource || "filter_top",
                    }, state);
                }
                emitFilterChanged(state);
                break;
            }
            case vehicleReset.type: {
                emitInternalEvent("vehicle_reset", null, state);
                emitFilterChanged(state);
                break;
            }
            case categorySet.type: {
                const categoryKey = action.payload.categoryKey;
                const title = action.payload.selectedCategory;
                if (categoryKey || title) {
                    // without a known click source the selection came in via URL
                    emitInternalEvent("category_filtered", {
                        selected_category: categoryPathTitles(state, categoryKey, title),
                        selection_source: action.payload.selectionSource || takeCategorySelectionSource() || "deeplink",
                    }, state);
                }
                break;
            }
            case categoryReset.type: {
                emitInternalEvent("category_reset", null, state);
                break;
            }
            case markerSet.type: {
                if (Array.isArray(action.payload) && action.payload.includes("SALE")) {
                    // without a known click source the sale route was opened via URL
                    emitInternalEvent("category_sale", {
                        selection_source: takeCategorySelectionSource() || "deeplink",
                    }, state);
                }
                emitFilterChanged(state);
                break;
            }
            case filterSet.type:
            case sortSet.type: {
                emitFilterChanged(state);
                break;
            }
            case searchSet.type: {
                if (action.payload.searchPhrase) {
                    emitInternalEvent("search", {search_phrase: action.payload.searchPhrase}, state);
                }
                break;
            }
            case productSet.type: {
                emitInternalEvent("product_view", {product: toProductDetails(action.payload.product)}, state);
                break;
            }
            case shareButtonClicked.type: {
                emitInternalEvent("share_product", {product: toProductDetails(action.payload)}, state);
                break;
            }
            case enquirySaved.type: {
                emitInternalEvent("product_inquiry_sent", {product: toProductDetails(action.payload.product)}, state);
                break;
            }
            case bookmarkAdded.type: {
                emitInternalEvent("favs_add", {product: toProductDetails(action.payload)}, state);
                break;
            }
            case bookmarkRemoved.type: {
                emitInternalEvent("favs_remove", {product: toProductDetails(action.payload)}, state);
                break;
            }
            case wishlistSharingSaved.type: {
                emitInternalEvent("favs_inquiry_sent", {
                    products: (action.payload.products || []).map(product => toProductDetails(product)),
                }, state);
                break;
            }
            case toCartAdded.type: {
                emitInternalEvent("cart_add", {
                    product: toProductDetails(action.payload.product, {
                        amount: 1,
                        shippingMethod: action.payload.shippingMethod,
                    }),
                }, state);
                break;
            }
            case fromCartRemoved.type: {
                emitInternalEvent("cart_remove", {
                    product: toProductDetails(action.payload, {
                        amount: removedCartItem ? removedCartItem.amount : 1,
                        shippingMethod: removedCartItem ? removedCartItem.shippingMethod : null,
                    }),
                }, state);
                break;
            }
            case quantitySet.type: {
                // quantity spinner in the cart: report the (clamped) difference as add/remove
                const item = state.cart.items.find(item => item.product.id === action.payload.id);
                if (item && previousQuantity != null) {
                    const delta = item.amount - previousQuantity;
                    const payload = {
                        product: toProductDetails(item.product, {
                            amount: Math.abs(delta),
                            shippingMethod: item.shippingMethod,
                        }),
                    };
                    if (delta > 0) emitInternalEvent("cart_add", payload, state);
                    if (delta < 0) emitInternalEvent("cart_remove", payload, state);
                }
                break;
            }
            case cartSuccessChecked.type: {
                emitInternalEvent("cart_view", {products: cartProducts(state)}, state);
                break;
            }
            case couponSuccessChecked.type: {
                const coupon = action.payload.coupon;
                if (coupon && CouponState.isValid(coupon.couponState) && coupon.coupon && coupon.coupon.couponNo) {
                    emitInternalEvent("coupon_added", {coupon_no: coupon.coupon.couponNo.index}, state);
                }
                break;
            }
            case checkoutPageStepSet.type: {
                const step = action.payload;
                if (step !== previousCheckoutPage) {
                    if (step === CHECKOUT_PAGE_STATUS.ADRESS) {
                        emitInternalEvent("cart_address_enter", null, state);
                    }
                    if (step === CHECKOUT_PAGE_STATUS.OVERVIEW) {
                        emitInternalEvent("cart_payment_confirmed", {
                            selected_pmt_method: (state.order.payment || {}).paymentProvider,
                        }, state);
                    }
                }
                break;
            }
            case paymentSet.type: {
                if (action.payload && action.payload.paymentProvider) {
                    emitInternalEvent("cart_payment_selected", {
                        selected_pmt_method: action.payload.paymentProvider,
                    }, state);
                }
                break;
            }
            case paymentSuccessVerified.type: {
                const order = state.order;
                emitInternalEvent("purchase_complete", {
                    total_price: order.totalBillingAmount.cost,
                    shipping_costs: order.orderItemsPriceSumWithShipping.cost - order.orderItemsPriceSum.cost,
                    transaction_id: order.order.orderNo,
                    products: orderProducts(order),
                    selected_pmt_method: (order.payment || {}).paymentProvider,
                }, state);
                break;
            }
            default:
                break;
        }
    } catch (e) {
        console.error(e);
    }

    return result;
};
